import React, { PropsWithChildren } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useSearchParam } from "react-use";

import { LogoutRoute } from "../auth/LogoutRoute";
import { useLogout } from "../hooks/useLogout";

import AppLoading from "./AppLoading";
import LoginOidc from "./login/LoginOidc";
import LoginSso from "./login/LoginSso";

type Props = PropsWithChildren<{
  enableOidc?: boolean;
}>;

/**
 * This is the route that Classlink redirects to for completing a login. This
 * route then redirects to `/login/sso` when a token is successfully retrieved.
 * */
export const LoginOidcRoute = () => {
  const navigate = useNavigate();
  const params = useParams<{ provider: string }>();
  const code = useSearchParam("code");
  const logout = useLogout();

  if (code == null || params.provider == null) {
    return <AppLoading />;
  }

  return (
    <LoginOidc
      code={code}
      provider={params.provider}
      onToken={(token) => navigate(`/login/sso?token=${token}`)}
      onLogout={() => logout("/login")}
    />
  );
};

export const LoginSsoRoute = () => {
  const token = useSearchParam("token");
  const navigate = useNavigate();

  if (token == null) {
    return <AppLoading />;
  }

  return (
    <LoginSso
      token={token}
      onAuthenticated={() => {
        // There seems to be a race condition where already-logged-in
        // users fail to redirect (maybe because the logic that removes the
        // token from the URL happens in the same tick as the redirect).
        // there's probably a better way to handle this, but for now...
        setTimeout(() => navigate("/login"), 200);
      }}
    />
  );
};

const AppRoutes = ({ enableOidc, children }: Props) => {
  return (
    <Routes>
      {enableOidc && (
        <Route path="/login/oidc/:provider" element={<LoginOidcRoute />} />
      )}
      <Route path="/login/sso" element={<LoginSsoRoute />} />
      <Route path="/logout" element={<LogoutRoute />} />
      {children}
    </Routes>
  );
};

export default AppRoutes;
